import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AuthContext } from '@/lib/auth/models/auth.model';
import { Tables } from '@/integrations/supabase/types';
import { ItineraryData, ItineraryStatus } from '@/lib/builder/models/itinerary.model';
import { ActivityFactory, ItineraryActivity } from '@/lib/builder/models/activity.model';


export type Itinerary = Tables<'itineraries'> & {
  pref_email?: string;
  pref_name?: string;
  pref_surname?: string;
  td_email?: string;
  td_name?: string;
  td_surname?: string;
  contact_email: string;
  contact_first_name?: string;
  contact_last_name?: string;
  travel_request_id: string;
};

interface UseItinerariesOptions {
  statusFilter?: string;
  excludeStatus?: string;
  disableAutoFetch?: boolean; // Nuovo parametro per disabilitare il caricamento automatico
}

// Helper function to copy all storage files for an itinerary using edge function
async function copyItineraryStorageFiles(
  oldItineraryId: string,
  newItineraryId: string,
  activityIdMap: Record<string, string>
) {
  console.log('🗂️ Starting storage files copy for itinerary:', {
    oldItineraryId,
    newItineraryId,
    activityMapSize: Object.keys(activityIdMap).length
  });
  
  try {
    // Fetch activities from OLD itinerary (con media/documentation originali)
    const { data: oldActivities, error: oldError } = await supabase
      .from('itinerary_activities')
      .select('id, media, documentation')
      .eq('itinerary_id', oldItineraryId);

    if (oldError || !oldActivities) {
      console.error('❌ Error fetching old activities for file copy:', oldError);
      return;
    }

    console.log(`📊 Found ${oldActivities.length} old activities to process`);

    // Prepara array di attività da copiare (solo quelle con file)
    const activitiesToCopy = oldActivities
      .filter(act => activityIdMap[act.id] && (act.media?.length || act.documentation?.length))
      .map(act => ({
        oldActivityId: act.id,
        newActivityId: activityIdMap[act.id],
        media: act.media || [],
        documentation: act.documentation || []
      }));

    if (activitiesToCopy.length === 0) {
      console.log('Nessuna attività con file da copiare');
      return;
    }

    // Log dettagliato delle attività da copiare
    console.log('Activities to copy (FE → Edge):', activitiesToCopy);

    // Chiamata unica alla edge function
    const { data, error } = await supabase.functions.invoke('copy-itinerary-files', {
      body: {
        bucket: 'p2b',
        oldItineraryId,
        newItineraryId,
        activities: activitiesToCopy
      }
    });

    if (error) {
      console.error('❌ Edge function error:', error);
      return;
    }

    if (data?.success) {
      console.log(`✅ Copied ${data.count} files`);
    }

    console.log(`✨ Storage files copy completed!`);
  } catch (error) {
    console.error('❌ Error copying storage files:', error);
  }
}

export const useItineraries = (options: UseItinerariesOptions = {}) => {
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();

  // Usa useRef per memorizzare i valori delle opzioni e evitare loop infiniti
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Recupera l'utente dal contesto di autenticazione
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useItineraries deve essere usato all\'interno di un AuthProvider');
  }

  // Recupera gli itinerari dell'utente corrente
  const fetchItineraries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('[useItineraries] fetchItineraries: INIZIO');
      let query = supabase
        .from('complete_itinerary_info')
        .select('*');
      if (optionsRef.current.statusFilter) {
        query = query.eq('status', optionsRef.current.statusFilter);
      }
      if (optionsRef.current.excludeStatus) {
        query = query.neq('status', optionsRef.current.excludeStatus);
      }
      const { data, error: fetchError } = await query.order('created_at', { ascending: false });
      if (fetchError) {
        console.error('[useItineraries] fetchItineraries: ERRORE', fetchError);
        setError(t('dashboard.loadItinerariesError'));
        toast({
          title: t('common.error'),
          description: t('dashboard.loadItinerariesError'),
          variant: "destructive",
        });
        return;
      }
      console.log('[useItineraries] fetchItineraries: OK', data?.length ?? 0);
      setItineraries(data ?? []);
    } catch (err) {
      console.error('[useItineraries] fetchItineraries: UNEXPECTED ERROR', err);
      setError(t('errors.generic'));
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Carica gli itinerari al mount e quando le opzioni cambiano (solo se non disabilitato)
  useEffect(() => {
    if (!options.disableAutoFetch) {
      fetchItineraries();
    } else {
      // Se il caricamento automatico è disabilitato, imposta loading a false
      setLoading(false);
    }
  }, [options.statusFilter, options.excludeStatus, options.disableAutoFetch, fetchItineraries]);

  // Funzione per aggiornare i dati
  const refreshData = useCallback(async () => {
    await fetchItineraries();
  }, [fetchItineraries]);

  // Funzione per salvare/aggiornare un itinerario
  const saveItinerary = useCallback(async (itineraryData: ItineraryData) => {
    try {
      let result;
      console.log('[useItineraries] saveItinerary: INIZIO', itineraryData.id ? 'UPDATE' : 'INSERT', itineraryData);
      if (itineraryData.id) {
        const { data, error } = await supabase
          .from('itineraries')
          .update(itineraryData.getPayload())
          .eq('id', itineraryData.id)
          .select()
          .single();
        if (error) {
          console.error('[useItineraries] saveItinerary: ERRORE UPDATE', error);
          toast({
            title: t('common.error'),
            description: t('itinerary.saveUpdateError'),
            variant: "destructive",
          });
          return null;
        }
        result = data;
        setItineraries(prev => prev.map(itinerary =>
          itinerary.id === data.id ? { ...itinerary, ...data } : itinerary
        ));
      } else {
        const { data, error } = await supabase
          .from('itineraries')
          .insert(itineraryData.getPayload())
          .select()
          .single();
        if (error) {
          console.error('[useItineraries] saveItinerary: ERRORE INSERT', error);
          toast({
            title: t('common.error'),
            description: t('itinerary.saveCreateError'),
            variant: "destructive",
          });
          return null;
        }
        result = data;
        setItineraries(prev => [data, ...prev]);
      }
      console.log('[useItineraries] saveItinerary: OK', result);
      return result;
    } catch (err) {
      console.error('[useItineraries] saveItinerary: UNEXPECTED ERROR', err);
      toast({
        title: t('common.error'),
        description: t('itinerary.saveUnexpectedError'),
        variant: "destructive",
      });
      return null;
    }
  }, []);

  // Funzione per eliminare un itinerario
  const deleteItinerary = useCallback(async (itineraryId: string) => {
    try {
      console.log('[useItineraries] deleteItinerary: INIZIO', itineraryId);
      const { error } = await supabase
        .from('itineraries')
        .delete()
        .eq('id', itineraryId);
      if (error) {
        console.error('[useItineraries] deleteItinerary: ERRORE', error);
        toast({
          title: t('common.error'),
          description: t('itinerary.deleteError'),
          variant: "destructive",
        });
        return false;
      }
      setItineraries(prev => prev.filter(itinerary => itinerary.id !== itineraryId));
      toast({
        title: t('notifications.itineraryDeleted'),
        description: t('notifications.itineraryDeleted'),
      });
      console.log('[useItineraries] deleteItinerary: OK', itineraryId);
      return true;
    } catch (err) {
      console.error('[useItineraries] deleteItinerary: UNEXPECTED ERROR', err);
      toast({
        title: t('common.error'),
        description: t('errors.generic'),
        variant: "destructive",
      });
      return false;
    }
  }, []);

  // Function to fetch existing itinerary by requestId from database
  const fetchItinerary = useCallback(async (requestId: string) => {
    try {
      console.log('[useItineraries] fetchItinerary: INIZIO', requestId);
      const { data, error } = await supabase
        .from('itineraries')
        .select('*')
        .eq('travel_request_id', requestId)
        .single();
      if (error && error.code !== 'PGRST116') {
        console.error('[useItineraries] fetchItinerary: ERRORE', error);
        return null;
      }
      console.log('[useItineraries] fetchItinerary: OK', data);
      return data;
    } catch (error) {
      console.error('[useItineraries] fetchItinerary: UNEXPECTED ERROR', error);
      return null;
    }
  }, []);

  // Function to fetch itinerary activities
  const fetchItineraryActivities = useCallback(async (itineraryId: string) => {
    try {
      console.log('[useItineraries] fetchItineraryActivities: INIZIO', itineraryId);
      const { data, error } = await supabase
        .from('itinerary_activities')
        .select('*')
        .eq('itinerary_id', itineraryId)
        .order('date', { ascending: true })
        .order('location', { ascending: true });
      if (error) {
        console.error('[useItineraries] fetchItineraryActivities: ERRORE', error);
        return [];
      }
      console.log('[useItineraries] fetchItineraryActivities: OK', data?.length ?? 0);
      return (data || []).map(activity => ActivityFactory.create(activity as ItineraryActivity));
    } catch (error) {
      console.error('[useItineraries] fetchItineraryActivities: UNEXPECTED ERROR', error);
      return [];
    }
  }, []);

  /**
   * Nuova funzione: salva le attività con insert, update, delete separati
   * @param itineraryId
   * @param toInsert array di nuove attività da inserire
   * @param toUpdate array di attività da aggiornare (devono avere id)
   * @param toDelete array di id attività da cancellare
   */
  const saveItineraryActivities = useCallback(
    async (
      itineraryId: string,
      toInsert: ItineraryActivity[],
      toUpdate: ItineraryActivity[],
      toDelete: string[]
    ) => {
      try {
        console.log('[useItineraries] saveItineraryActivities: INIZIO', { itineraryId, toInsert: toInsert.length, toUpdate: toUpdate.length, toDelete: toDelete.length });
        // INSERT + UPDATE together: build a combined payload and perform one upsert
        if (toInsert.length > 0 || toUpdate.length > 0) {
          const insertPayload = toInsert.map(activity => {
            const { inOrOutActivity, is_multi_day, ...activityData } = activity as any;
            return {
              ...activityData,
              itinerary_id: itineraryId
            };
          });

          const updatePayload = toUpdate.map(activity => {
            const { inOrOutActivity, is_multi_day, ...activityData } = activity as any;
            // ensure itinerary_id exists for updated rows as well
            return {
              ...activityData,
              itinerary_id: activityData.itinerary_id || itineraryId
            };
          }).filter(a => a && a.id);

          const combinedPayload = [...insertPayload, ...updatePayload];

          if (combinedPayload.length > 0) {
            const { error: upsertError } = await supabase
              .from('itinerary_activities')
              .upsert(combinedPayload);

            if (upsertError) {
              console.error('[useItineraries] saveItineraryActivities: ERRORE UPSERT (batch)', upsertError);
              toast({
                title: t('common.error'),
                description: t('itinerary_messages.cannotInsertActivities'),
                variant: 'destructive',
              });
              return false;
            }
            console.log('[useItineraries] saveItineraryActivities: OK UPSERT (batch)', combinedPayload.length);
          }
        }
        // DELETE
        if (toDelete.length > 0) {
          const { error: deleteError } = await supabase
            .from('itinerary_activities')
            .delete()
            .in('id', toDelete);
          if (deleteError) {
            console.error('[useItineraries] saveItineraryActivities: ERRORE DELETE', deleteError, toDelete);
            toast({
              title: t('common.error'),
              description: t('itinerary_messages.cannotDeleteActivities'),
              variant: 'destructive',
            });
            return false;
          }
          console.log('[useItineraries] saveItineraryActivities: OK DELETE', toDelete.length);
        }
        console.log('[useItineraries] saveItineraryActivities: FINE');
        return true;
      } catch (err) {
        console.error('[useItineraries] saveItineraryActivities: UNEXPECTED ERROR', err);
        toast({
        title: t('common.error'),
        description: t('itinerary_messages.unexpectedSaveError'),
        variant: 'destructive',
      });
        return false;
      }
    },
  []);

  // Function to update itinerary status
  const updateItineraryStatus = useCallback(async (itineraryId: string, newStatus: ItineraryStatus) => {
    try {
      console.log('[useItineraries] updateItineraryStatus: INIZIO', itineraryId, newStatus);
      const { error } = await supabase
        .from('itineraries')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', itineraryId);
      if (error) {
        console.error('[useItineraries] updateItineraryStatus: ERRORE', error);
        toast({
          title: t('common.error'),
          description: t('itinerary.updateStatusError'),
          variant: "destructive",
        });
        return false;
      }
      setItineraries(prev => prev.map(itinerary =>
        itinerary.id === itineraryId
          ? { ...itinerary, status: newStatus, updated_at: new Date().toISOString() }
          : itinerary
      ));
      console.log('[useItineraries] updateItineraryStatus: OK', itineraryId, newStatus);
      return true;
    } catch (err) {
      console.error('[useItineraries] updateItineraryStatus: UNEXPECTED ERROR', err);
      toast({
        title: t('common.error'),
        description: t('itinerary.updateStatusUnexpectedError'),
        variant: "destructive",
      });
      return false;
    }
  }, []);

  // Function to duplicate an itinerary with all its activities (truly transactional)
  const duplicateItinerary = useCallback(async (itineraryId: string) => {
    try {
      console.log('[useItineraries] duplicateItinerary: INIZIO', itineraryId);
      const { data: originalItinerary, error: fetchError } = await supabase
        .from('itineraries')
        .select('title')
        .eq('id', itineraryId)
        .single();
      console.log('[useItineraries] duplicateItinerary: Fetched original', originalItinerary);
      if (fetchError) {
        console.error('[useItineraries] duplicateItinerary: ERRORE FETCH', fetchError);
        toast({
          title: t('common.error'),
          description: t('itinerary.duplicateFetchError'),
          variant: "destructive",
        });
        return null;
      }
      const { data: result, error: rpcError } = await supabase.rpc('duplicate_itinerary', {
        p_itinerary_id: itineraryId,
        p_new_title: `${originalItinerary.title} (Copy)`
      });
      if (rpcError) {
        console.error('[useItineraries] duplicateItinerary: ERRORE RPC', rpcError);
        toast({
          title: t('common.error'),
          description: t('itinerary.duplicateRpcError'),
          variant: "destructive",
        });
        return null;
      }
      if (result && result.new_itinerary) {
        console.log('[useItineraries] duplicateItinerary: NEW', result.new_itinerary);
        const newItinerary = result.new_itinerary as unknown as Itinerary;
        const activityIdMap = result.activity_id_map as Record<string, string>;
        const oldItineraryId = result.old_itinerary_id as string;
        console.log('[useItineraries] duplicateItinerary: activityIdMap', activityIdMap);
        setItineraries(prev => [newItinerary, ...prev]);
        copyItineraryStorageFiles(oldItineraryId, newItinerary.id, activityIdMap).catch(err => {
          console.error('[useItineraries] duplicateItinerary: Error copying storage files', err);
        });
        fetchItineraries();
      }
      console.log('[useItineraries] duplicateItinerary: FINE');
      return null;
    } catch (err) {
      console.error('[useItineraries] duplicateItinerary: UNEXPECTED ERROR', err);
      toast({
        title: t('common.error'),
        description: t('itinerary.duplicateUnexpectedError'),
        variant: "destructive",
      });
      return null;
    }
  }, []);

  return {
    itineraries,
    loading,
    error,
    refreshData,
    saveItinerary,
    deleteItinerary,
    fetchItinerary,
    fetchItineraryActivities,
    saveItineraryActivities,
    duplicateItinerary,
    updateItineraryStatus
  };
};
